const knownNoisyIssues = [
  'WonderPush',
  'Non-Error',
  "Can't find variable: ga",
  'ga is not defined',
  'ga.getAll is not a function',
  'Non-Error promise rejection captured with keys: currentTarget, detail, isTrusted, target',
  'GSI_LOGGER',
  'FedCM',
  'AbortError',
  'Failed to fetch (api.btloader.com)',
  'Load failed (api.btloader.com)',
  'NetworkError when attempting to fetch resource. (api.btloader.com)',
  'Failed to fetch (analytics.google.com)',
  'Failed to fetch (www.google-analytics.com)',
  'Failed to fetch (googletagmanager.com)',
  'Failed to fetch (pagead2.googlesyndication.com)',
  "Event `CustomEvent` (type=unhandledrejection) captured as promise rejection",
  "Failed to read a named property 'performance' from 'Window': An attempt was made to break through the security policy of the user agent",
  "Failed to read a named property 'performance' from 'Window': Blocked a restricted frame with origin",
  "Failed to read a named property 'clearTimeout' from 'Window': An attempt was made to break through the security policy of the user agent",
  "Failed to read a named property 'clearTimeout' from 'Window': Blocked a restricted frame with origin",
  'URIError',
  'URI malformed',
  'Load failed',
  'Got unexpected null or undefined',
  'Java bridge method invocation error',
  // Firefox-only XPCOM internal error thrown by third-party ad/analytics scripts
  // (ads/js/tfav_adl, api/init-*, analytics) when the engine tears a component
  // down mid-scroll. Never raised by first-party code, so it is pure noise.
  'NS_ERROR_NOT_INITIALIZED',
]

// Common Sentry configuration.
// NOTE: tracing/replay sample rates were removed on purpose. They were dead
// config: @sentry/browser v7 does not register BrowserTracing or Replay by
// default, and no `integrations` array adds them, so the rates did nothing.
const sentryConfig = {
  dsn: 'https://5eb8d58117d14638858f28b39d3afe37@o159814.ingest.us.sentry.io/1222718',
  allowUrls: [
    /cgtrader\.com/,
    /assets\.cgtrader\.com/,
    /testcgt\.com/,
    /testcgt2\.com/,
    /testcgt3\.com/
  ],
  autoSessionTracking: true,
  ignoreErrors: knownNoisyIssues,
}

// @sentry/browser is loaded on demand (see initSentry) so its ~320 kB stay off
// the initial bundle. Until it resolves, capture calls and the current user are
// buffered and replayed once the SDK is ready.
let sentryRef = null
let loadFailed = false
let pendingUser = null
const callQueue = []

function flushPendingUser() {
  if (sentryRef && pendingUser) {
    sentryRef.setUser(pendingUser)
  }
}

// Buffer a capture call until the SDK is ready, or forward it immediately.
// Only the methods used across the app (captureException, captureMessage) are
// proxied — see the synchronous window.Sentry stub below.
function proxyCall(method, args) {
  if (sentryRef) {
    return sentryRef[method](...args)
  }
  // SDK failed to load: drop the call instead of queueing forever.
  if (loadFailed) {
    return undefined
  }
  callQueue.push([method, args])
  return undefined
}

// Synchronous stand-in so the ~30 unguarded `window.Sentry.captureException`
// callers keep working during the 1-3 s before the real SDK loads. Replaced by
// the real module on load (see loadAndInit), so post-load callers get the full
// SDK surface.
const sentryStub = {
  captureException: (...args) => proxyCall('captureException', args),
  captureMessage: (...args) => proxyCall('captureMessage', args),
}

// Initialize Sentry with optional custom config
export const initSentry = (window, customConfig = {}) => {
  const config = { ...sentryConfig, ...customConfig }

  // Synchronous references so error handlers always have something callable,
  // even before the SDK has loaded. Queued calls are replayed once it inits.
  window.Sentry = window.Sentry || sentryStub
  window.sendErrorToSentry = (error, { tags, extra } = {}) => proxyCall('captureException', [error, { tags, extra }])

  const loadAndInit = () => {
    import(/* webpackChunkName: "sentry" */ '@sentry/browser')
      .then((Sentry) => {
        sentryRef = Sentry
        window.Sentry = Sentry
        Sentry.init(config)
        flushPendingUser()
        callQueue.splice(0).forEach(([method, args]) => Sentry[method](...args))
      })
      .catch(() => {
        // SDK failed to load (network/adblock): drop the queue and stop buffering
        // so later capture calls don't grow the queue unbounded.
        loadFailed = true
        callQueue.length = 0
      })
  }

  // Delay loading + initialization until the browser is idle to keep it off the
  // critical path and out of the initial bundle parse.
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(loadAndInit, { timeout: 3000 })
  } else {
    window.setTimeout(loadAndInit, 1000)
  }
}

// Set user information (buffered until the SDK has loaded).
export const setSentryUser = (uuid, username) => {
  if (uuid && uuid.length > 0) {
    pendingUser = { id: uuid, username }
    flushPendingUser()
  }
}
